/**
 * PriceSetSidebar — left panel listing all price sets.
 * Supports search, selection, duplicate, and delete actions.
 */
import {
    Box,
    VStack,
    Text,
    Input,
    InputGroup,
    InputLeftElement,
    Icon,
    HStack,
    Badge,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    IconButton,
    Skeleton,
    Button,
    Divider,
    useDisclosure,
    AlertDialog,
    AlertDialogOverlay,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogBody,
    AlertDialogFooter,
    useToast,
    useColorModeValue,
} from '@chakra-ui/react';
import { useRef, useState } from 'react';
import {
    MdSearch,
    MdMoreVert,
    MdContentCopy,
    MdDelete,
    MdShare,
    MdAdd,
    MdInventory,
} from 'react-icons/md';
import { useDuplicatePriceSet, useDeletePriceSet, useUpdatePriceSet } from '@/services/hooks';

interface PriceSet {
    id: number;
    name: string;
    description?: string;
    category?: string;
    is_shared: boolean;
    items_count: number;
}

interface Props {
    sets: PriceSet[];
    isLoading: boolean;
    selectedId: number | null;
    onSelect: (id: number) => void;
    onNew: () => void;
}

export function PriceSetSidebar({ sets, isLoading, selectedId, onSelect, onNew }: Props) {
    const toast = useToast();
    const [search, setSearch] = useState('');
    const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
    const cancelRef = useRef<HTMLButtonElement>(null);

    const duplicateMutation = useDuplicatePriceSet();
    const deleteMutation = useDeletePriceSet();
    const updateMutation = useUpdatePriceSet();

    const filtered = sets.filter((s) =>
        s.name.toLowerCase().includes(search.toLowerCase()) ||
        (s.category || '').toLowerCase().includes(search.toLowerCase())
    );

    const handleDuplicate = async (id: number, e: React.MouseEvent) => {
        e.stopPropagation();
        try {
            const newSet = await duplicateMutation.mutateAsync(id);
            toast({ title: 'Price set duplicated', status: 'success', duration: 2000 });
            onSelect(newSet.id);
        } catch {
            toast({ title: 'Failed to duplicate', status: 'error', duration: 2000 });
        }
    };

    const handleDeleteConfirm = async () => {
        if (!pendingDeleteId) return;
        try {
            await deleteMutation.mutateAsync(pendingDeleteId);
            if (selectedId === pendingDeleteId) onSelect(sets.find(s => s.id !== pendingDeleteId)?.id ?? 0);
            toast({ title: 'Price set deleted', status: 'success', duration: 2000 });
        } catch {
            toast({ title: 'Failed to delete', status: 'error', duration: 2000 });
        } finally {
            onDeleteClose();
            setPendingDeleteId(null);
        }
    };

    const handleToggleShare = async (id: number, currentShared: boolean, e: React.MouseEvent) => {
        e.stopPropagation();
        try {
            await updateMutation.mutateAsync({ id, data: { is_shared: !currentShared } });
            toast({ title: currentShared ? 'Set made private' : 'Set shared with org', status: 'success', duration: 2000 });
        } catch {
            toast({ title: 'Failed to update', status: 'error', duration: 2000 });
        }
    };

    // Color mode values
    const bgVal = useColorModeValue('white', 'navy.800');
    const borderVal = useColorModeValue('gray.200', 'navy.700');
    const searchBg = useColorModeValue('gray.50', 'navy.700');
    const headerTitleColor = useColorModeValue('gray.600', 'gray.300');
    const emptyIconColor = useColorModeValue('gray.400', 'gray.600');
    const emptyTextColor = useColorModeValue('gray.500', 'gray.500');
    const itemHoverBg = useColorModeValue('gray.50', 'navy.700');
    const itemNameColor = useColorModeValue('gray.800', 'gray.100');
    const itemMetaColor = useColorModeValue('gray.500', 'gray.500');
    const menuIconColor = useColorModeValue('gray.500', 'gray.400');
    const skeletonStart = useColorModeValue('gray.100', 'navy.700');
    const skeletonEnd = useColorModeValue('gray.200', 'navy.600');
    const modalBg = useColorModeValue('white', 'navy.800');
    const modalBorder = useColorModeValue('gray.200', 'navy.600');
    const modalTextColor = useColorModeValue('gray.600', 'gray.300');

    return (
        <Box
            h="100%"
            display="flex"
            flexDir="column"
            borderRight="1px solid"
            borderColor={borderVal}
            bg={bgVal}
        >
            {/* Header */}
            <Box px={4} pt={5} pb={3}>
                <HStack justify="space-between" mb={3}>
                    <Text fontWeight="700" fontSize="sm" color={headerTitleColor} textTransform="uppercase" letterSpacing="wider">
                        My Sets
                    </Text>
                    <IconButton
                        aria-label="New price set"
                        icon={<MdAdd />}
                        size="xs"
                        variant="ghost"
                        colorScheme="brand"
                        onClick={onNew}
                    />
                </HStack>
                <InputGroup size="sm">
                    <InputLeftElement pointerEvents="none">
                        <Icon as={MdSearch} color="gray.500" />
                    </InputLeftElement>
                    <Input
                        placeholder="Search sets..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        bg={searchBg}
                        border="none"
                        _focus={{ boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                        borderRadius="md"
                        pl={8}
                    />
                </InputGroup>
            </Box>

            <Divider borderColor={borderVal} />

            {/* Set list */}
            <VStack flex="1" overflowY="auto" spacing={0} align="stretch" px={2} py={2}>
                {isLoading ? (
                    [1, 2, 3].map((i) => (
                        <Skeleton key={i} height="56px" borderRadius="lg" mb={1} startColor={skeletonStart} endColor={skeletonEnd} />
                    ))
                ) : filtered.length === 0 ? (
                    <Box textAlign="center" py={10}>
                        <Icon as={MdInventory} boxSize={8} color={emptyIconColor} mb={2} />
                        <Text fontSize="xs" color={emptyTextColor}>
                            {search ? 'No matches' : 'No price sets yet'}
                        </Text>
                    </Box>
                ) : (
                    filtered.map((set) => {
                        const isSelected = set.id === selectedId;
                        return (
                            <Box
                                key={set.id}
                                px={3}
                                py={2.5}
                                borderRadius="lg"
                                cursor="pointer"
                                bg={isSelected ? 'brand.500' : 'transparent'}
                                _hover={{ bg: isSelected ? 'brand.500' : itemHoverBg }}
                                transition="all 0.15s"
                                onClick={() => onSelect(set.id)}
                                position="relative"
                                role="group"
                            >
                                <HStack justify="space-between" align="flex-start">
                                    <Box minW={0}>
                                        <Text
                                            fontSize="sm"
                                            fontWeight="600"
                                            color={isSelected ? 'white' : itemNameColor}
                                            noOfLines={1}
                                        >
                                            {set.name}
                                        </Text>
                                        <HStack mt={0.5} spacing={2}>
                                            <Text fontSize="xs" color={isSelected ? 'brand.100' : itemMetaColor}>
                                                {set.items_count} items
                                            </Text>
                                            {set.is_shared && (
                                                <Badge
                                                    size="xs"
                                                    colorScheme={isSelected ? 'whiteAlpha' : 'blue'}
                                                    variant="subtle"
                                                    fontSize="9px"
                                                >
                                                    Shared
                                                </Badge>
                                            )}
                                            {set.category && (
                                                <Badge
                                                    size="xs"
                                                    colorScheme={isSelected ? 'whiteAlpha' : 'purple'}
                                                    variant="subtle"
                                                    fontSize="9px"
                                                >
                                                    {set.category}
                                                </Badge>
                                            )}
                                        </HStack>
                                    </Box>
                                    <Menu>
                                        <MenuButton
                                            as={IconButton}
                                            aria-label="Options"
                                            icon={<MdMoreVert />}
                                            variant="ghost"
                                            size="xs"
                                            opacity={0}
                                            _groupHover={{ opacity: 1 }}
                                            color={isSelected ? 'white' : menuIconColor}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                        <MenuList zIndex={999}>
                                            <MenuItem
                                                icon={<MdContentCopy />}
                                                onClick={(e) => handleDuplicate(set.id, e)}
                                            >
                                                Duplicate
                                            </MenuItem>
                                            <MenuItem
                                                icon={<MdShare />}
                                                onClick={(e) => handleToggleShare(set.id, set.is_shared, e)}
                                            >
                                                {set.is_shared ? 'Make Private' : 'Share with Org'}
                                            </MenuItem>
                                            <MenuItem
                                                icon={<MdDelete />}
                                                color="red.400"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setPendingDeleteId(set.id);
                                                    onDeleteOpen();
                                                }}
                                            >
                                                Delete
                                            </MenuItem>
                                        </MenuList>
                                    </Menu>
                                </HStack>
                            </Box>
                        );
                    })
                )}
            </VStack>

            {/* Footer CTA */}
            <Box px={3} pb={4}>
                <Button
                    leftIcon={<MdAdd />}
                    size="sm"
                    w="100%"
                    variant="outline"
                    colorScheme="brand"
                    onClick={onNew}
                >
                    New Price Set
                </Button>
            </Box>

            {/* Delete confirm dialog */}
            <AlertDialog isOpen={isDeleteOpen} leastDestructiveRef={cancelRef} onClose={onDeleteClose}>
                <AlertDialogOverlay>
                    <AlertDialogContent bg={modalBg} borderColor={modalBorder} borderWidth="1px">
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">Delete Price Set</AlertDialogHeader>
                        <AlertDialogBody color={modalTextColor}>
                            This will permanently delete the price set and all its line items. This cannot be undone.
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelRef} variant="ghost" onClick={onDeleteClose}>Cancel</Button>
                            <Button
                                colorScheme="red"
                                ml={3}
                                onClick={handleDeleteConfirm}
                                isLoading={deleteMutation.isPending}
                            >
                                Delete
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Box>
    );
}
